import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Moon, Sun, Siren, LogOut, User, Map as MapIcon, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import NewJobSheet from "./NewJobSheet";
import GlobalSearch from "./GlobalSearch";
// NewImpoundDialog no longer imported — button moved out of Topbar (Aug 2026).
// EmailIntakeDialog + IntakeInboxPill removed (Feb 2026) — the "Import from
// Email" button + attention pill were retired when the review-queue was
// nuked. The dialog now opens via the Sidebar "Email Intake" item
// through IntakeDialogProvider at the app-shell level.
import YardConfirmPill from "./YardConfirmPill";
import ProfileDialog from "./ProfileDialog";
import { api, getOncallStatus, setOncallConfig } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { tenantLocale, tenantTz } from "../lib/company";
import { hasCap } from "../lib/roles";

// Uniform sizing for topbar action buttons on the Dashboard view — all match
// the On-Call/Away button's width and the New Impound button's height.
const DASH_BTN = "w-44 h-10 justify-center";

const THEME_KEY = "dispatch_theme";
const SN_LAST_SEEN_KEY = "sn_last_seen_closure";

export default function Topbar() {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  // App default = dark mode. Toggle in the topbar persists the choice per device.
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "dark");
  const [oncall, setOncall] = useState(null);
  const loc = useLocation();
  const nav = useNavigate();
  const { isStaff, user, logout, refreshUser } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);

  const onLogout = () => {
    logout();
    nav("/login", { replace: true });
  };
  const initials = user?.name
    ? user.name.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()
    : "U";
  const roleLabel = hasCap(user, "admin") ? "Admin"
    : user?.role === "dispatcher" ? "Dispatcher"
    : user?.role === "truck" ? "Driver"
    : "User";

  // Poll on-call status every 60s so the badge stays current even when
  // mode flips at 4pm or someone toggles the manual override remotely.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getOncallStatus();
        if (!cancelled) setOncall(s);
      } catch { /* non-fatal */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Poll for jobs that First Rescue closed on their end while we were watching
  // them. The backend's SN poller auto-mirrors the close into our DB and stamps
  // `auto_closed_source/auto_closed_at`. We toast each new one so the dispatcher
  // notices the dispatch window updating in real time.
  useEffect(() => {
    if (!isStaff) return undefined;
    // Initialise last-seen to "now" so we don't fire toasts for historical closures on first load.
    if (!localStorage.getItem(SN_LAST_SEEN_KEY)) {
      localStorage.setItem(SN_LAST_SEEN_KEY, new Date().toISOString());
    }
    let cancelled = false;
    const check = async () => {
      try {
        const since = localStorage.getItem(SN_LAST_SEEN_KEY);
        const { data } = await api.get("/jobs/recent-sn-closures", { params: { since } });
        if (cancelled) return;
        const closures = data?.closures || [];
        if (closures.length === 0) return;
        // closures are sorted desc; advance pointer to the newest one
        const newest = closures[0]?.auto_closed_at;
        if (newest) localStorage.setItem(SN_LAST_SEEN_KEY, newest);
        // Toast oldest → newest so order reads naturally
        closures.slice().reverse().forEach((c) => {
          const label = c.work_order_number || c.customer_name || "Job";
          const verb = c.status === "cancelled" ? "cancelled" : "completed";
          toast.info(`${label} ${verb} in First Rescue`, {
            description: "Removed from the dispatch window — see History.",
          });
        });
        // Nudge any open page to refresh its job list
        window.dispatchEvent(new CustomEvent("jobs:refresh"));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[Topbar] recent-sn-closures poll failed:", err?.message || err);
        }
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isStaff]);

  // Apply theme to <html> on mount + when toggled.
  // CRITICAL: must set BOTH `data-theme` attribute (drives index.css CSS variables
  // for shadcn portals like Sheet/Dialog/Popover) AND `.dark` class (drives every
  // Tailwind `dark:` utility — tailwind.config.js uses darkMode:["class"]).
  // Setting only one of them silently breaks half the dark-mode styling and is
  // the cause of all "writing is invisible in dark mode" bug reports.
  // If you change this, also update DriverPage.jsx which has the same logic.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <header
      className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4"
      data-testid="app-topbar"
    >
      {/* Global search — hidden on tablet (< dtop = 1281 px) because the
       * Dashboard mounts a larger yard-friendly copy in the KPI slot.
       * Kept on desktop so search stays reachable from every route. */}
      <div className="flex-1 max-w-md hidden dtop:block">
        <GlobalSearch size="md" testidPrefix="topbar" />
      </div>
      {/* On tablet the KPI-slot search takes over the "hero" role. This
       * spacer keeps the right-hand button cluster right-aligned when
       * the topbar search is hidden. */}
      <div className="flex-1 dtop:hidden" />

      <div className={cn("hidden lg:flex items-center justify-center gap-2 font-display text-sm text-slate-700 rounded-md bg-slate-100", DASH_BTN)}>
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="font-bold tracking-tight">LIVE</span>
        <span className="text-slate-500 font-medium">
          {now.toLocaleTimeString(tenantLocale(), { timeZone: tenantTz(), hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" })}
        </span>
      </div>

      {/* Yard-confirm pill — hidden entirely when 0 pending. Sits next
          to the On-Call badge so both "attention needed" nudges live
          together on every route. */}
      <YardConfirmPill />

      {/* Live Map + Refresh live in every route so the top-bar stays
          visually consistent (was previously only on "/"). Live Map
          hops us to Dashboard first if we're elsewhere, since that's
          where the full-screen overlay is mounted. Moved to the RH
          cluster (Feb 2026) alongside On-Call / Theme / Refresh / Admin. */}

      <button
        onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        className="p-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors hidden"
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        data-testid="topbar-theme-toggle"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-600" />}
      </button>

      <Button
        onClick={() => setOpen(true)}
        className={cn("bg-slate-900 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50 font-semibold ml-auto", DASH_BTN)}
        data-testid="topbar-new-job-btn"
      >
        <Plus className="h-4 w-4.5" strokeWidth={2.5} />
        New Job
      </Button>

      {/* "Import from Email" button removed (Feb 2026) — moved to the
          Sidebar "Email Intake" item (opens the same paste-and-parse
          dialog via IntakeDialogProvider). Keeps top-bar breathable. */}

      {/* "New Impound" was removed here (Aug 2026) to free up top-bar space —
          Impounds page has its own equivalent button, no need to duplicate.
          Keeps the top-bar breathable on smaller viewports. */}

      {/* On-Call badge — moved to the RH cluster (Feb 2026) so it sits
          alongside Live Map / Theme / Refresh / Admin. Live Map has
          ml-auto to push the whole right cluster flush right. */}
      <Button
        variant="outline"
        onClick={() => {
          if (loc.pathname !== "/") {
            nav("/");
            setTimeout(() => window.dispatchEvent(new CustomEvent("livemap:open")), 50);
          } else {
            window.dispatchEvent(new CustomEvent("livemap:open"));
          }
        }}
        className={cn("font-semibold ml-auto hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50", DASH_BTN)}
        title="Open full-screen live map (ESC to exit)"
        data-testid="topbar-live-map-btn"
      >
        <MapIcon className="h-4 w-4.5" />
        Live Map
      </Button>

      {oncall && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-md font-bold text-[10px] sm:text-xs uppercase tracking-wider transition-colors",
                DASH_BTN,
                oncall.is_on_call
                  ? "bg-slate-900 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
                  : "bg-slate-200 text-slate-700 border border-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
              )}
              title={`On-call mode is ${oncall.is_on_call ? "ACTIVE" : "OFF"} (${oncall.reason}). Click to change.`}
              data-testid="topbar-oncall-badge"
            >
              <Siren className="h-3.5 w-3.5 shrink-0" />
              <span>Afterhours</span>
              <span className="hidden sm:inline opacity-80 font-semibold normal-case">
                · {oncall.reason === "manual" ? "Force ON"
                   : oncall.reason === "off-manual" ? "Force OFF"
                   : oncall.reason === "holiday" ? "Holiday"
                   : "Auto"}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="oncall-menu">
            <DropdownMenuLabel>After-Hours / On-Call</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {[
              { val: true, label: "Force ON", testid: "oncall-menu-force-on", active: oncall.config?.manual_override === true },
              { val: null, label: "Auto (Schedule)", testid: "oncall-menu-auto", active: oncall.config?.manual_override == null },
              { val: false, label: "Force OFF", testid: "oncall-menu-force-off", active: oncall.config?.manual_override === false },
            ].map((opt) => (
              <DropdownMenuItem
                key={opt.label}
                data-testid={opt.testid}
                className={opt.active ? "font-bold" : ""}
                onSelect={async () => {
                  try {
                    const cfg = oncall.config || {};
                    const next = await setOncallConfig({
                      manual_override: opt.val,
                      weekday_start_hour: cfg.weekday_start_hour,
                      weekday_start_minute: cfg.weekday_start_minute,
                      weekday_end_hour: cfg.weekday_end_hour,
                      weekday_end_minute: cfg.weekday_end_minute,
                      weekends_oncall: cfg.weekends_oncall,
                      holidays_oncall: cfg.holidays_oncall,
                    });
                    setOncall(next);
                    window.dispatchEvent(new CustomEvent("oncall:changed", { detail: next }));
                    toast.success(
                      opt.val === true ? "On-Call FORCED ON"
                      : opt.val === false ? "On-Call FORCED OFF"
                      : "Following schedule",
                    );
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to update on-call");
                  }
                }}
              >
                {opt.label}{opt.active && " ✓"}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => nav("/settings")} data-testid="oncall-menu-settings">
              Edit schedule…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Theme toggle — icon-only square button matching Refresh, sits in
          the RH cluster alongside Refresh + Admin (Feb 2026 rearrange). */}
      <button
        onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        className="h-10 w-10 p-0 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50 transition-colors flex items-center justify-center shrink-0"
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        data-testid="topbar-theme-toggle-rh"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-600" />}
      </button>

      {/* Refresh — dispatches jobs:refresh globally; any page that
          listens (Dashboard, Jobs Queue, History) picks it up. On pages
          that don't listen the event is a harmless no-op. Icon-only,
          floats to the right edge alongside the user menu. */}
      <Button
        variant="outline"
        onClick={() => window.dispatchEvent(new CustomEvent("jobs:refresh"))}
        className="h-10 w-10 p-0 shrink-0 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
        title="Refresh page data"
        aria-label="Refresh"
        data-testid="topbar-refresh-btn"
      >
        <RefreshCw className="h-4 w-4.5" />
      </Button>

      {/* User menu — ALWAYS visible (incl. mobile, where sidebar is hidden).
          Critical: without this, mobile admins/dispatchers have no way to sign
          out because Sidebar (`hidden md:flex`) is the only other logout.
          Moved to the very right of the top bar (Feb 2026). */}
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50 transition-colors flex items-center gap-2 px-2 shrink-0",
                "text-slate-900 dark:text-slate-100"
              )}
              title={`${user.name || "User"} · ${roleLabel}`}
              data-testid="topbar-user-menu"
              aria-label="Account menu"
            >
              <span className="h-7 w-7 rounded-full bg-slate-700 dark:bg-slate-600 flex items-center justify-center font-display font-bold text-white text-xs shrink-0">
                {initials}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-tight pr-1">
                <span className="text-sm font-semibold truncate max-w-[110px]">{user.name || "User"}</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{roleLabel}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold truncate">{user.name || "User"}</div>
              <div className="text-xs text-slate-500 font-normal flex items-center gap-1 mt-0.5">
                <User className="h-3 w-3" /> {roleLabel}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setProfileOpen(true)}
              data-testid="topbar-profile-btn"
            >
              <User className="h-4 w-4 mr-2" /> Your details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onLogout}
              className="text-rose-700 dark:text-rose-300 focus:text-rose-800 focus:bg-rose-50 dark:focus:bg-rose-950/40"
              data-testid="topbar-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ProfileDialog
        person={user || null}
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onSaved={() => refreshUser?.()}
      />

      <NewJobSheet open={open} onOpenChange={setOpen} />
      {/* NewImpoundDialog removed from Topbar (Aug 2026) — the "New Impound"
          button lived here; the Impounds page keeps its own equivalent.
          EmailIntakeDialog moved to the app-shell via IntakeDialogProvider
          (Feb 2026 email-intake refit). */}
    </header>
  );
}
